<script setup lang="ts">
import { addWeeks, startOfWeek, addDays } from 'date-fns'
import { to_dd_MMMM } from '@/utils/helpers';

const storeU = useUtilisateurStore()
const display = useDisplay()

const selectedWeek = defineModel<Date>(undefined, new Date())
const numberOfDays = defineModel<number>('numberOfDays', 7)
const weekStart = computed(() => getWeekStart())
const weekEnd = computed(() => getWeekEnd())

const showDatePicker = ref(false)

// All days in the selected week — passed to the date picker for full-week highlighting
const weekDays = computed(() => {
  const start = getWeekStart()
  return Array.from({ length: numberOfDays.value ?? 7 }, (_, i) => addDays(start, i))
})

function getWeekStart() { return startOfWeek(selectedWeek.value ?? new Date(), { weekStartsOn: 0 }) }
function getWeekEnd() {
  const start = new Date(getWeekStart().getTime())
  const addDaysCount = (numberOfDays.value ?? 7) - 1
  start.setDate(start.getDate() + addDaysCount)
  return start
}

function previousWeek() {
  const newDate = addWeeks(selectedWeek.value ?? new Date(), -1)
  if (newDate.getFullYear() === storeU.annéeSélectionnée) {
    selectedWeek.value = newDate
  }
}
function nextWeek() {
  const newDate = addWeeks(selectedWeek.value ?? new Date(), 1)
  if (newDate.getFullYear() === storeU.annéeSélectionnée) {
    selectedWeek.value = newDate
  }
}
function selectWeek(date: Date) { selectedWeek.value = startOfWeek(date, { weekStartsOn: 0 }) }

// Called when the user clicks a date in the picker.
// If a date outside the current week is clicked, select that date's week.
// If a date inside the current week is clicked, Vuetify will try to deselect it —
// we ignore that and keep the current selection.
function onDateClick(newDates: Date[]) {
  if (newDates.length > weekDays.value.length) {
    const currentSet = new Set(weekDays.value.map(d => d.getTime()))
    const clicked = newDates.find(d => !currentSet.has(d.getTime()))
    if (clicked) selectWeek(clicked)
  }
  // If length decreased, user clicked within the current week — no action needed.
  // weekDays is still computed from selectedWeek, so the picker re-renders correctly.
}

function allowedDates(val: any) {
  return val.getFullYear() === storeU.annéeSélectionnée
}

watch(() => storeU.annéeSélectionnée, (newYear) => {
  if (selectedWeek.value === undefined) {
    const today = new Date()
    selectedWeek.value = new Date(newYear, today.getMonth(), today.getDate())
  }
  else if (selectedWeek.value.getFullYear() !== newYear) {
    selectedWeek.value = new Date(newYear, selectedWeek.value.getMonth(), selectedWeek.value.getDate())
  }
})

defineExpose({
  getWeekStart,
  getWeekEnd
})
</script>

<template>
  <div class="d-flex align-center">
    <v-icon v-if="!display.smAndDown" color="primary" icon="M6 1H8V3H16V1H18V3H19C20.11 3 21 3.9 21 5V19C21 20.11 20.11 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.89 3.89 3 5 3H6V1M5 8V19H19V8H5M7 10H17V12H7V10Z" class="me-1" size="28"/>
    <v-btn color="primary" variant="tonal" :size="display.smAndDown ? 'small' : 'default'" class="me-1" icon @click="previousWeek">
      <v-icon color="primary" icon="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" size="20"/>
    </v-btn>
    <v-btn
      color="primary" rounded="lg" variant="tonal"
      :min-width="display.smAndDown ? 'auto' : '220'"
      :size="display.smAndDown ? 'small' : 'default'"
      class="me-1"
      @click="showDatePicker = true">
      <span class="text-capitalize">{{ to_dd_MMMM(weekStart) }} – {{ to_dd_MMMM(weekEnd) }} {{ weekEnd.getFullYear() }}</span>
    </v-btn>
    <v-btn color="primary" variant="tonal" :size="display.smAndDown ? 'small' : 'default'" class="me-1" icon @click="nextWeek">
      <v-icon color="primary" icon="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" size="20"/>
    </v-btn>
    <template v-if="!display.smAndDown">
      <v-divider vertical class="my-1 mx-1" style="height: 28px; align-self: center;"/>
      <YearSelector class="me-1"/>
    </template>
  </div>

  <v-dialog v-model="showDatePicker" max-width="360px" :fullscreen="display.smAndDown">
    <v-card rounded="xl" class="overflow-hidden pa-0">

      <div class="week-picker-header">
        <div class="text-overline week-picker-header__label">Semaine sélectionnée</div>
        <div class="text-h5 font-weight-bold week-picker-header__date">
          {{ to_dd_MMMM(weekStart) }} – {{ to_dd_MMMM(weekEnd) }}
        </div>
        <v-btn
          density="comfortable"
          icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
          variant="text"
          class="week-picker-header__close"
          color="white"
          @click="showDatePicker = false"
        />
      </div>

      <v-date-picker
        :model-value="weekDays"
        multiple
        :first-day-of-week="1"
        show-adjacent-months
        :allowed-dates="allowedDates"
        hide-header
        color="primary"
        width="100%"
        elevation="0"
        @update:model-value="onDateClick"
      />

      <v-card-actions class="px-4 pb-4 pt-1 gap-2">
        <YearSelector v-if="display.smAndDown"/>
        <v-spacer />
        <v-btn variant="text" rounded="lg" @click="showDatePicker = false">Annuler</v-btn>
        <v-btn color="primary" variant="flat" rounded="lg" @click="showDatePicker = false">OK</v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<style scoped>
.week-picker-header {
  position: relative;
  background: rgb(var(--v-theme-primary));
  padding: 20px 24px 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.week-picker-header__label {
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.1em;
  font-size: 0.7rem;
}
.week-picker-header__date {
  color: #fff;
}
.week-picker-header__close {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0.8;
}
.week-picker-header__close:hover {
  opacity: 1;
}
</style>
